<template>
  <Layout>
    <template #body>
      <Card
        title="Formulario de contacto"
      >
        <template #body>
          <div class="card-register">
            <Form
              v-if="formConfig?.items?.length"
              ref="$formRef"
              :items="formConfig?.items"
            >
              <template #emailValidation__after>
                <div
                  v-if="showErrorEmailValidationRef"
                  class="field__form--error"
                  role="alert"
                >
                  Las direcciones de correo electrónico no coinciden
                </div>
              </template>
            </Form>
            <div
              v-if="showErrorEmailValidationGeneralRef"
              class="field__form--error"
              role="alert"
            >
              Se debe de proporcionar al menos una dirección de correo
              electrónico o un número de teléfono.
            </div>
            <div class="card-register__keypad">
              <Button
                class="bttn-AJ card-register__keypad__bttn p-button-secondary"
                label="Volver"
                tabindex="0"
                @click="returnDetailRequest"
                @keyup.enter="returnDetailRequest"
              />
              <Button
                class="bttn-AJ card-register__keypad__bttn card-register__keypad__bttn--primary"
                :label="'Editar'"
                tabindex="0"
                @click="submit"
                @keyup.enter="submit"
              />
            </div>
          </div>
        </template>
      </Card>
    </template>
  </Layout>
</template>

<script setup>
// #region VUE
import {
  inject,
  onMounted,
  reactive,
  shallowRef,
  toRaw,
} from 'vue';
// #endregion

// #region STTEDRA
import {
  Form,
  useDev,
  useNormalizer,
} from '@sttedra_ui/framework';
// #endregion

// #region COMPONENTS COMPOSABLES
import useFormConfig from './form.view-config';

import {useDebounceFn} from '@vueuse/core';

import Button from 'primevue/button/sfc';
import Card from '@/components/card/Card.vue';
import Layout from '@/components/layout/Layout.vue';
import useManageApiError from '@/composables/manage-api-error';

import {useRouter} from 'vue-router';

import EditRegisterContactViewState from './edit-register-contact.view-state';
// #endregion

// #region API
import {StatusCodes as HttpStatus} from 'http-status-codes';
import {
  updateConsolidateContact as updateConsolidateContactApi,
} from '@/api/register/register.api';
// #endregion

// #region Props, Emits
// ...
// #endregion

// #region Params, Ref, Reactives, Computed
const appState$$ = inject('appState$$');
const $formRef = shallowRef(null);
const formConfig = useFormConfig();

const showErrorEmailValidationRef = shallowRef(false);
const showErrorEmailValidationGeneralRef = shallowRef(false);

const state = new EditRegisterContactViewState();
const state$$ = reactive(state);

const router = useRouter();
const {getObjectFilled} = useNormalizer();

const {
  showErrorToast,
} = useManageApiError();
// #endregion

// #region Functions
const returnDetailRequest = useDebounceFn(returnDetailRequestFn, 200, {maxWait: 700});
function returnDetailRequestFn() {
  appState$$.setLoginApp(true);
  router.push({name: `DetailRequest`});
}

const submit = useDebounceFn(submitFn, 200, {maxWait: 700});
async function submitFn() {
  try {
    const invalid = await secondFormValidate();

    if (invalid) return;

    const {
      email,
      phone,
    } = toRaw($formRef.value.state$$.values);

    const {response} = await updateConsolidateContactApi({
      identification: appState$$.detailRequest.identification,
      email,
      phone,
    },
    appState$$.detailRequest.temporalToken);

    if (response.status === HttpStatus.OK) {
      appState$$.setLoginApp(true);
      router.push({name: `DetailRequest`});
      return;
    }

    showErrorToast('Ocurrió un error al actualizar el contacto. Por favor, inténtelo de nuevo.');
  } catch (error) {
    useDev.error('[EditRegisterContact.vue] submit: Unexpected error:', error);
  }
}
async function secondFormValidate() {
  const fields = [
    $formRef.value.v$Ref.email,
    $formRef.value.v$Ref.emailValidation,
    $formRef.value.v$Ref.phone,
  ];

  // Validate all fields
  await $formRef.value.v$Ref.$validate();

  if (fields.some((field) => field.$invalid)) {
    return true;
  }

  const {email, emailValidation, phone} = toRaw(
    $formRef.value.state$$.values,
  );

  if (email && email !== emailValidation) {
    showErrorEmailValidationRef.value = true;
    return true;
  }

  const emailField = document.querySelector('.form__item--email input');
  const phoneField = document.querySelector('.form__item--phone input');

  if (!phone && !email) {
    if (emailField) {
      emailField.classList.add('p-invalid');
    }

    if (phoneField) {
      phoneField.classList.add('p-invalid');
    }

    showErrorEmailValidationGeneralRef.value = true;
    return true;
  }

  const regex = /^\+\d{2,3} ?\d{3} ?\d{3} ?\d{3}$/;
  if (phone && !regex.test(phone)) {
    phoneField.classList.add('p-invalid');
    return true;
  }

  if (emailField) {
    emailField.classList.remove('p-invalid');
  }

  if (phoneField) {
    phoneField.classList.remove('p-invalid');
  }

  showErrorEmailValidationGeneralRef.value = false;
  showErrorEmailValidationRef.value = false;

  return false;
}
// #endregion

// #region Effects, Hooks
onMounted(async () => {
  state$$.isVisible = false;

  try {
    if (appState$$.isOffline || !getObjectFilled(appState$$.detailRequest) || !getObjectFilled(state)) {
      appState$$.setLoginApp(true);
      router.push({name: `Home`});
      return;
    }
  } catch (error) {
    useDev.error('[DetailRequest.vue] onMounted', error);
  }

  state$$.isVisible = true;
  appState$$.setLoginApp(false);
});
// #endregion

// #region Provide, Expose
// ...
// #endregion
</script>

<style lang="scss" scoped>
@use '@/styles/vars' as *;
@use '@/styles/breakpoints' as *;
.card-register{
  display: flex;
  flex-direction: column;
  gap: $brand__boxspace--medium;
  padding: $brand__boxspace--medium;

  &__keypad{
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    gap: ($brand__boxspace--medium * 1);
    justify-content: center;
    align-items: center;

    @include media("<=phone") {
      -webkit-box-align: center;
          -ms-flex-align: center;
              align-items: center;
      -webkit-box-orient: vertical;
      -webkit-box-direction: reverse;
          -ms-flex-direction: column-reverse;
              flex-direction: column-reverse;
      -webkit-box-pack: center;
          -ms-flex-pack: center;
              justify-content: center;
    }

    @include media(">tablet") {
      -webkit-box-orient: horizontal;
      -webkit-box-direction: normal;
          -ms-flex-direction: row;
              flex-direction: row;
      -webkit-box-pack: center;
          -ms-flex-pack: center;
              justify-content: center;
    }

    &__bttn{
      width: max-content;
      &--primary{
        background-color: var(--primary-800);
      }
    }
  }
  .field__form--error{
    width: 15vw;
    min-width: 281px;

   @include media("<=phone") {
      width: 100%;
    }
  }
}

</style>
